import { newLocalId } from '@/lib/protocol-ids';
import type { VersionedDraftKit } from '@/lib/versioned-draft';
import { canSaveVersion } from '@/lib/versioned-draft';
import type {
  ProtocolAnalyst,
  ProtocolContent,
  ProtocolDetail,
  ProtocolDraftContent,
  ProtocolRangement,
  ProtocolStepDraft,
} from '@matter_extractor/shared';
import {
  DEFAULT_STEP_RULES,
  PROTOCOL_ANALYST_DEFAULT,
  PROTOCOL_ANALYST_TEMPLATE,
  PROTOCOL_RANGEMENT_EMPTY,
  rangementTargetsKept,
} from '@matter_extractor/shared';

/**
 * What the Protocoles editor holds while somebody types, and the eight
 * operations its two surfaces ask for (refonte modulaire §7.1, §16 H17).
 *
 * The comparison that greys « Enregistrer », the version numbering and the six
 * common refusals are in `versioned-draft.ts` and hold for every versioned
 * object. What is here is the SHAPE of a protocol's draft, the two functions
 * that cross between it and the wire, and the step-level edits - because those
 * are asked for by THREE hands (the frise, the step editor, the page) and a
 * reorder written twice would be two contracts.
 *
 * ## Why the draft is not a `ProtocolContent`
 *
 * A `ProtocolContent` is a version: its name, its description and every step
 * name are non-empty, and its objectives are named. A DRAFT is a half-written
 * thought - a step somebody has just inserted and not named, an objective
 * being typed, a turn cap nobody has posed. Keeping the two shapes apart is what
 * lets the editor render exactly what was typed while `normalizedContent`
 * decides, once, what would be sent.
 *
 * ## What is a SHAPE here and what is a PROBLEM elsewhere
 *
 * `normalizedContent` answers `null` on the four things a `ProtocolContent`
 * cannot express - an empty name, an empty description, an unnamed step, an
 * unnamed objective. It does NOT look at the turn cap, at an objective without
 * a criterion, or at a jump aimed at a step that has gone: those are legal
 * shapes and refused REASONS, named one by one by `protocolProblems` in
 * `@matter_extractor/shared` and listed by the screen (§7.1). Folding them in
 * here would have made the save box grey without saying why, which is the one
 * thing §7.1 spends a paragraph refusing.
 *
 * ## A protocol always holds at least one step
 *
 * `protocolContentSchema` says `min(1)`, and the empty editor opens on « Étape
 * 1 » (§7.1). So `removeStep` on the last remaining step REPLACES it with a
 * fresh empty one rather than leaving none: an editor with no step has no frise
 * to insert into and no card to press, which is a dead end reachable by one
 * click on a trash.
 */

export interface ProtocolDraft {
  readonly name: string;
  readonly description: string;
  /** The steps IN ORDER - the frise draws this list, and the order is the meaning. */
  readonly steps: readonly ProtocolStepDraft[];
  /**
   * The reading of BEFORE #196 (« Lecture pour l'analyste », noyau.md §4.9, #154),
   * carried through every crossing of the wire so a save never drops what another
   * hand wrote - the MCP writes it too - and so that `compat:prod`, which reads
   * every version in production back, never meets a code that dropped it (K14).
   * It is neither served nor edited any more: the section Analyste shows it
   * folded, with a button that copies it into the reading of the pass 1. Empty on
   * a protocol of before #154 and on one created since #196.
   */
  readonly analystReading: string;
  /**
   * « Rangement » (scenario-succes.md §4, #177): the objectives the rangeur
   * files the conversation onto and the consigne that says how, carried through
   * every crossing of the wire for the reason `analystReading` is. Empty on a
   * protocol of before #177.
   */
  readonly rangement: ProtocolRangement;
  /**
   * « Analyste » (analyste.md §3, #196): the thresholds, the weight of the style and
   * the two readings, carried through every crossing of the wire for the reason
   * `analystReading` is - the assistant writes it too. A protocol of before #196
   * reads the defaults and two empty readings. The LEVEL of each objective is on
   * the objective itself.
   */
  readonly analyst: ProtocolAnalyst;
}

/**
 * A step nobody has typed yet: named by its rank, with the default rules and
 * nothing else (§7.1, « une étape 1 vide dans la frise »).
 *
 * `index` names it - « Étape 3 » - rather than leaving it blank, because the
 * frise labels a card with its name and a card labelled with nothing reads as
 * broken. It is a real value in the draft and not a placeholder: an operator
 * who keeps it gets a step called « Étape 3 », which is what the mockup shows.
 */
export function newStep(index: number): ProtocolStepDraft {
  return {
    id: newLocalId(),
    name: `Étape ${index + 1}`,
    // Never a farewell step by default, in a new protocol or a new step (H85).
    farewell: false,
    // Nor a step that rereads on entry (issue #165, H93): the case is opt-in.
    rereadOnEntry: false,
    // Nor a step that hides its objectives (issue #191, H18): opt-in too.
    hideObjectives: false,
    direction: '',
    objectives: [],
    rules: DEFAULT_STEP_RULES,
    conditions: [],
  };
}

/**
 * What the empty editor opens on - and a FUNCTION rather than a constant.
 *
 * A step carries an id struck by `newLocalId()`, so a module-level constant
 * would hand every new protocol of a browser session the same step id. Two
 * editors open in two tabs would then write two v1s whose steps collide the
 * moment one of them is cited by the other's jump - and nothing on either screen
 * would show it.
 */
export function emptyProtocolDraft(): ProtocolDraft {
  return {
    name: '',
    description: '',
    steps: [newStep(0)],
    // No reading of before #196: it is not served, and a protocol born now has
    // nothing to carry over - the generic reading of #154 would only draw its grey
    // block on a protocol that was never written before.
    analystReading: '',
    rangement: PROTOCOL_RANGEMENT_EMPTY,
    // A protocol created from now on is BORN with the two generic readings of the
    // section, and no level yet (analyste.md §3.8, K15).
    analyst: PROTOCOL_ANALYST_TEMPLATE,
  };
}

/**
 * A draft from a stored content - a version's, or the server draft's.
 *
 * The parameter is `ProtocolDraftContent` - the shared name for a partial
 * content - and not a locally derived `Partial<ProtocolContent>`, because the
 * two are NOT the same type under `exactOptionalPropertyTypes`: the local one
 * promises that a present key holds a value, while a draft off the wire carries
 * `... | undefined`. One vocabulary on both sides of the round trip is what
 * keeps them from drifting.
 *
 * A draft with NO step gets one, for the reason the head of this file gives: an
 * editor with an empty frise has no card to press.
 */
export function draftFromContent(content: ProtocolDraftContent): ProtocolDraft {
  const steps = content.steps ?? [];
  return {
    name: content.name ?? '',
    description: content.description ?? '',
    steps: steps.length === 0 ? [newStep(0)] : [...steps],
    analystReading: content.analystReading ?? '',
    rangement: content.rangement ?? PROTOCOL_RANGEMENT_EMPTY,
    analyst: content.analyst ?? PROTOCOL_ANALYST_DEFAULT,
  };
}

/**
 * What the editor opens on: the server draft when there is one, the
 * `développement` version otherwise, and an empty editor for a protocol that
 * does not exist yet (§4, §7.1).
 *
 * The order is the pattern's: a draft « protège d'un rechargement », so it wins
 * over the version it was started from.
 */
export function openingDraft(detail: ProtocolDetail | null): ProtocolDraft {
  if (detail === null) return emptyProtocolDraft();
  return draftFromContent(detail.draft?.content ?? detail.developmentContent);
}

/** A step with its name trimmed and its objectives' names trimmed with it. */
function normalizedStep(step: ProtocolStepDraft): ProtocolStepDraft {
  return {
    ...step,
    name: step.name.trim(),
    objectives: step.objectives.map((objective) => ({
      ...objective,
      name: objective.name.trim(),
    })),
  };
}

/**
 * The content a draft would become, or `null` when it could not become one.
 *
 * Trimmed first, then judged - the order `protocolContentSchema` states: a name
 * holding three spaces is a name an operator believes they wrote, and it would
 * reach the base as a blank. `null` means one of the four REQUIRED strings is
 * empty, which is the whole of what a shape can refuse (see the head).
 *
 * Everything else travels as it stands. The rules, the conditions and the
 * criteria are kept verbatim: they are what the version stores, and a
 * normalisation here would be a second implementation of the server's.
 */
export function normalizedContent(draft: ProtocolDraft): ProtocolContent | null {
  const name = draft.name.trim();
  const description = draft.description.trim();
  if (name === '' || description === '') return null;
  if (draft.steps.length === 0) return null;
  const steps = draft.steps.map(normalizedStep);
  const holed = steps.some(
    (step) => step.name === '' || step.objectives.some((objective) => objective.name === ''),
  );
  if (holed) return null;
  return {
    name,
    description,
    steps,
    analystReading: draft.analystReading.trim(),
    // The targets the steps still carry (§4: a removed objective leaves the
    // list at the save, without a refusal) - the very function the server's
    // write schema runs, so the box and the base never name two lists.
    rangement: rangementTargetsKept(steps, {
      ...draft.rangement,
      consigne: draft.rangement.consigne.trim(),
    }),
    analyst: {
      ...draft.analyst,
      functionsReading: draft.analyst.functionsReading.trim(),
      styleReading: draft.analyst.styleReading.trim(),
    },
  };
}

/**
 * What an autosave SENDS - and the one thing about it that is not obvious.
 *
 * `protocolDraftContentSchema` makes each field optional WITHOUT loosening it:
 * `name` is still `z.string().trim().min(1)` when it is there. So an empty name
 * has to be OMITTED and cannot be sent as `''` - a draft of a protocol nobody
 * has named yet would otherwise be answered with a 400, which is the one refusal
 * an autosave must never produce: it fires while somebody types, and a red box
 * on the third keystroke of an empty field teaches an operator that the editor
 * is broken. The description goes the same way.
 *
 * `steps` is ALWAYS sent, empty or not, because the whole of what somebody is
 * building lives there: omitting it would make « j'ai retiré l'étape 4 »
 * unsaveable. A step's own name is allowed to be empty by the draft schema, so
 * it travels untouched.
 */
export function draftPayload(draft: ProtocolDraft): ProtocolDraftContent {
  const payload: ProtocolDraftContent = {
    steps: [...draft.steps],
    // Always sent, empty included: an empty reading is a legal draft, and
    // leaving it out would let a reload bring back one somebody erased.
    analystReading: draft.analystReading,
    // Always sent, for the same reason.
    rangement: draft.rangement,
    analyst: draft.analyst,
  };
  const name = draft.name.trim();
  if (name !== '') payload.name = name;
  const description = draft.description.trim();
  if (description !== '') payload.description = description;
  return payload;
}

/** Everything the versioned editor needs to run a protocol's draft (§4). */
export const PROTOCOL_DRAFT_KIT: VersionedDraftKit<
  ProtocolContent,
  ProtocolDraftContent,
  ProtocolDraft
> = {
  opening: openingDraft,
  fromContent: draftFromContent,
  normalized: normalizedContent,
  payload: draftPayload,
};

/**
 * Is « Enregistrer une nouvelle version » usable?
 *
 * The three refusals of §4, plus the one H17 (§16) adds and that belongs to a
 * NEW protocol only: « s'active quand le nom, la description et le plafond de
 * chaque étape sont posés ». On an EXISTING protocol the cap stays a problem
 * listed by the pill and refused by the box - it does not grey the button,
 * because a protocol that already has a v1 has a v2 worth writing even while one
 * of its nine steps is half-typed, and a grey button there would hide a draft
 * that is otherwise ready behind a step nobody is looking at.
 */
export function canSaveProtocol(draft: ProtocolDraft, detail: ProtocolDetail | null): boolean {
  if (!canSaveVersion(draft, detail, PROTOCOL_DRAFT_KIT)) return false;
  if (detail !== null) return true;
  return draft.steps.every((step) => step.rules.maxTurns !== null);
}

/* ------------------------------------------------------ the step edits */

/**
 * A new step at slot `index` - « on insère entre deux, au début ou à la fin »
 * (§7.1).
 *
 * The index is the SLOT the « + » stands in: 0 is before the first step, and
 * `steps.length` is after the last. It is clamped rather than trusted, because
 * the frise computes it from a layout and a draft can shrink under a click that
 * was aimed at the frise a moment earlier.
 *
 * The new step is named from its slot, so an insertion in the middle reads as
 * « Étape 3 » where it lands rather than where the list ends. The names of the
 * steps AFTER it are left alone: a name is a name and not a rank, and renaming
 * what somebody wrote because a neighbour appeared would be an edit nobody
 * asked for.
 */
export function insertStep(
  steps: readonly ProtocolStepDraft[],
  index: number,
): ProtocolStepDraft[] {
  const at = Math.max(0, Math.min(index, steps.length));
  return [...steps.slice(0, at), newStep(at), ...steps.slice(at)];
}

/**
 * The step at `fromIndex` moves so that it sits at `toIndex` in the RESULTING
 * list (§7.1, « on réordonne par glisser-déposer »).
 *
 * That is the contract `ProtocolTimelineProps.onMove` declares, and it is the
 * one worth spelling out: the naive reading - « insert it before the element
 * currently at `toIndex` » - answers differently depending on which side the
 * step came from, so a drag one slot to the right would appear to do nothing.
 * Removing first and inserting into the shortened list is what makes the index
 * mean the same thing in both directions.
 *
 * Out-of-range indices answer the list unchanged rather than throwing: this runs
 * on a pointer gesture, and a drop the layout mis-measured is not an error worth
 * a blank screen.
 */
export function moveStep(
  steps: readonly ProtocolStepDraft[],
  fromIndex: number,
  toIndex: number,
): ProtocolStepDraft[] {
  if (fromIndex < 0 || fromIndex >= steps.length) return [...steps];
  const moved = steps[fromIndex];
  if (moved === undefined) return [...steps];
  const rest = steps.filter((_, at) => at !== fromIndex);
  const to = Math.max(0, Math.min(toIndex, rest.length));
  return [...rest.slice(0, to), moved, ...rest.slice(to)];
}

/**
 * One step removed - and a protocol never falls to zero (see the head).
 *
 * The jumps that aimed at it are NOT rewritten: a jump names a step's id (§7.1),
 * a dangling one is a `jump-to-missing-step` problem the pill lists and the save
 * box refuses, and silently dropping the condition that carried it would delete
 * work nobody asked to delete.
 */
export function removeStep(steps: readonly ProtocolStepDraft[], id: string): ProtocolStepDraft[] {
  const rest = steps.filter((step) => step.id !== id);
  if (rest.length === steps.length) return [...steps];
  return rest.length === 0 ? [newStep(0)] : rest;
}

/** One step rewritten in place, found by its id - what `onChangeStep` applies. */
export function replaceStep(
  steps: readonly ProtocolStepDraft[],
  next: ProtocolStepDraft,
): ProtocolStepDraft[] {
  return steps.map((step) => (step.id === next.id ? next : step));
}

/**
 * Which step is selected once `removedId` is gone.
 *
 * The NEIGHBOUR and not the first step: an operator deleting step 4 of nine is
 * working around there, and being thrown back to « Accueil » costs them the
 * place they were reading. The one after it, or the one before when it was
 * last - and the first step of whatever is left when the selection was not the
 * removed one at all, which cannot happen from the step menu but can from a
 * frise whose draft moved under it.
 */
export function selectionAfterRemoval(
  steps: readonly ProtocolStepDraft[],
  removedId: string,
  selectedId: string,
): string {
  const rest = removeStep(steps, removedId);
  const first = rest[0]?.id ?? '';
  if (selectedId !== removedId) {
    return rest.some((step) => step.id === selectedId) ? selectedId : first;
  }
  const index = steps.findIndex((step) => step.id === removedId);
  if (index < 0) return first;
  return (rest[index] ?? rest[index - 1])?.id ?? first;
}
